import { Filter } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import {
  APPLIES_FILTER_PRESERVE_PARAM_KEYS,
  CHART_CONFIG,
  METRICS_CONFIG,
  PAGE_CONFIG,
  getAppliesUiComponents,
} from "./AppliesPageConfig";
import { useAppliesPage } from "./useAppliesPage";

import Icons from "@/components/icons";
import ModernPageLayout from "@/components/layout/ModernPageLayout";
import { Button } from "@/components/ui/v2/button";
import { FiltersDrawer } from "@/components/ui/v2/filters/FiltersDrawer";
import { HorizontalBarChart } from "@/components/ui/v2/horizontal-bar-chart";
import { TableActiveFilters } from "@/components/ui/v2/table/table-active-filters";
import UnavailablePage from "@/components/ui/v2/unavailable-page";
import MetricCard from "@/features/campaigns/jobboards/components/MetricCard";
import { useModuleStatus } from "@/features/integrations/api/useModuleStatus";
import {
  useAppliesFilteredCount,
  useApplyFilterOptions,
} from "@/features/integrations/modules/applies/api";
import ReportLevelTabs from "@/features/integrations/shared/components/ReportLevelTabs";
import { renderComponent } from "@/features/integrations/shared/renderer";
import { useValueFormatter } from "@/utils/useValueFormatter";

export default function AppliesPage() {
  const { t } = useTranslation();
  const { format: formatValue } = useValueFormatter();
  const { clientId } = useParams<{ clientId?: string }>();
  const { data: moduleStatus, isLoading: moduleLoading } = useModuleStatus("applies", { clientId });
  const {
    context,
    clientScoped,
    filtersOpen,
    setFiltersOpen,
    filterSections,
    activeFilters,
    removeFilter,
    clearFilters,
    filtersLoading,
    totalRecords,
    countBaseParams,
    filterKeys,
    filterOptionParams,
    totalDisplay,
    distinctJobBoards,
    distinctClients,
    metricsLoading,
    jobBoardChartData,
    chartLoading,
    searchParams,
  } = useAppliesPage({ clientId });

  // Thin wrapper so the drawer can invoke the count hook with its pending filter selection.
  // The hook itself lives at module scope in useAppliesFilteredCount; this binds the page-level
  // base params + filter keys to it.
  const useFilteredCount = (pending: Record<string, string[]>) =>
    useAppliesFilteredCount(countBaseParams, filterKeys, pending);

  // Same binding for the drawer's paged dimensions: the drawer calls this once per async section with
  // that section's key, its debounced search box, and whether it is expanded.
  const useAsyncFilterOptions = (dimension: string, search: string, enabled: boolean) =>
    useApplyFilterOptions(dimension, filterOptionParams, search, enabled);

  if (!moduleLoading && !moduleStatus?.enabled) {
    return <UnavailablePage variant="integrationDisabled" />;
  }

  const ui = getAppliesUiComponents(t, clientScoped);

  const filtersAction = (
    <Button variant="outline" size="sm" type="button" onClick={() => setFiltersOpen(true)}>
      <Filter className="h-4 w-4" />
      {t("common.filters", "Filters")}
    </Button>
  );

  return (
    <ModernPageLayout title={t(PAGE_CONFIG.titleKey)} subtitle={t(PAGE_CONFIG.subtitleKey)}>
      <div className="space-y-4">
        <ReportLevelTabs active="applies" />

        <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5">
          <MetricCard
            icon={<Icons.PersonIcon className="h-4 w-4 lg:h-6 lg:w-6 text-primary" />}
            label={t(METRICS_CONFIG.totalApplies.titleKey)}
            value={totalDisplay}
            variant="gradient"
            isLoading={metricsLoading}
          />
          <MetricCard
            icon={<Icons.ClientsIcon className="h-4 w-4 lg:h-6 lg:w-6 text-primary" />}
            label={t(METRICS_CONFIG.distinctJobBoards.titleKey)}
            value={formatValue(distinctJobBoards, "whole")}
            variant="gradient"
            isLoading={metricsLoading}
          />
          {!clientScoped && (
            <MetricCard
              icon={<Icons.UserIcon className="h-4 w-4 lg:h-6 lg:w-6 text-primary" />}
              label={t(METRICS_CONFIG.distinctClients.titleKey)}
              value={formatValue(distinctClients, "whole")}
              variant="gradient"
              isLoading={metricsLoading}
            />
          )}
        </div>

        {ui.map((component, index) => (
          <div key={`${component.component}-${index}`} className="space-y-4">
            {renderComponent(
              component,
              component.component === "tableToolbar"
                ? { ...context, customToolbarActions: filtersAction }
                : context
            )}
            {component.component === "tableToolbar" && activeFilters.length > 0 && (
              <TableActiveFilters
                filters={activeFilters}
                onRemoveFilter={removeFilter}
                onClearFilters={clearFilters}
              />
            )}
          </div>
        ))}

        <HorizontalBarChart
          data={jobBoardChartData}
          title={t(CHART_CONFIG.jobBoardChart.titleKey)}
          subtitle={t(CHART_CONFIG.jobBoardChart.subtitleKey)}
          tooltipLabel={t("common.applies")}
          isLoading={chartLoading}
          emptyMessage={context.emptyMessage}
          scale="log"
        />
      </div>

      <FiltersDrawer
        open={filtersOpen}
        onOpenChange={setFiltersOpen}
        searchParams={searchParams}
        filterKeys={[...filterKeys]}
        filterSections={filterSections}
        entityName={t("common.applies")}
        filtersLoading={filtersLoading}
        totalCount={totalRecords}
        useFilteredCount={useFilteredCount}
        useAsyncFilterOptions={useAsyncFilterOptions}
        preserveParamKeys={[...APPLIES_FILTER_PRESERVE_PARAM_KEYS]}
        searchTag={context.searchValue ?? undefined}
      />
    </ModernPageLayout>
  );
}
